/**
 * Single source of truth for the "merchandise subtotal" that every storefront
 * threshold (MOV gate, free-shipping bar) is measured against.
 *
 * NET, because Saleor filters the shipping methods on its own net-based rule:
 * free-over-250 is implemented as `maxOrder=249.99` on the paid methods, and
 * Saleor evaluates that against the base/net subtotal. If the UI gated on gross
 * while Saleor gates on net, the UI and the actually-available shipping methods
 * would disagree — we would promise free shipping that Saleor then does not
 * offer (or withhold one it does).
 *
 * This lives in one place on purpose: the gross->net meaning change has been
 * introduced piecemeal three times on this branch, each time missing a consumer.
 * Every threshold gate must read the basis from here, never inline its own.
 */

/** Structurally matches both the generated `TaxedMoney` and the looser cart-drawer prop. */
type TaxedMoneyish = {
	gross: { amount: number };
	net?: { amount: number } | null;
} | null;

/**
 * Resolves the net merchandise subtotal used for threshold comparisons.
 *
 * Falls back to gross so this stays a no-op while tax is still 0 everywhere,
 * then to `fallback` when no subtotal is available at all.
 */
export function resolveMerchandiseSubtotal(subtotalPrice: TaxedMoneyish | undefined, fallback = 0): number {
	return subtotalPrice?.net?.amount ?? subtotalPrice?.gross?.amount ?? fallback;
}
